import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../context/ToastContext";
import { getBulkConfirmEligible, bulkConfirmExpenseCaptures } from "../api";

const NAVY = "#0f172a";

const primaryBtnStyle: React.CSSProperties = {
  background: "#16a34a", color: "white", border: "none",
  padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};
const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};

export default function ExpenseBulkConfirmSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ["bulk-confirm-eligible"], queryFn: getBulkConfirmEligible });
  const captures = data?.captures ?? [];

  async function handleConfirmAll() {
    setConfirming(true);
    try {
      const result = await bulkConfirmExpenseCaptures(captures.map(c => c.id));
      qc.invalidateQueries({ queryKey: ["expense-captures"] });
      toast(`${result.count} expense${result.count !== 1 ? "s" : ""} confirmed`, "success");
      onDone();
    } catch (err: any) {
      toast(err?.response?.data?.detail || "Could not confirm — please try again", "error");
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "white", borderRadius: 12, padding: "24px 28px", width: 520, maxWidth: "95vw", maxHeight: "80vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
      >
        <h3 style={{ margin: "0 0 6px", color: NAVY, fontSize: 16 }}>Confirm all high-confidence</h3>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
          Every field read at 95%+ confidence, math reconciles, and the vendor has been confirmed at least 3 times before.
        </p>

        {isLoading && <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>Checking eligible captures…</div>}

        {!isLoading && captures.length === 0 && (
          <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>
            No captures currently meet the bar for bulk confirmation.
          </div>
        )}

        {!isLoading && captures.length > 0 && (
          <div style={{ display: "grid", gap: 8, marginBottom: 20 }}>
            {captures.map(c => (
              <div key={c.id} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: NAVY }}>{c.vendor_name}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {c.currency || "THB"} {(c.total || 0).toLocaleString("en", { minimumFractionDigits: 2 })} · {c.category}
                  {c.confidence_score != null && <> · {Math.round(c.confidence_score)}% confidence</>}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={outlineBtnStyle}>Close</button>
          {captures.length > 0 && (
            <button onClick={handleConfirmAll} disabled={confirming} style={primaryBtnStyle}>
              {confirming ? "Confirming…" : `Confirm all (${captures.length})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
